import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { List, ListItem, ListItemText, Collapse } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { ModuleCard, ModuleLink, navEntriesFor } from '../modules/moduleCatalog';
import { useModuleVisibility } from '../modules/useModuleVisibility';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';

interface SidebarNavProps {
    /** Icon-rail mode. Always false inside the mobile drawer, which has room for labels. */
    collapsed: boolean;
    /** Called after any navigation. The drawer uses it to close itself. */
    onNavigate?: () => void;
}

/** True when the current path belongs to this menu, so it starts expanded. */
const isMenuActive = (card: ModuleCard, links: ModuleLink[], pathname: string) => {
    const prefixes = [...links.map(l => l.path), ...(card.nav?.match ?? [])];
    // Exact match for every path (the dashboard Overview is "/", which prefixes everything);
    // prefix match only for deeper routes, so /attendance/employee still opens Attendance.
    return prefixes.some(p => pathname === p || (p !== '/' && pathname.startsWith(`${p}/`)));
};

/**
 * The nav body shared by the desktop rail and the mobile drawer.
 *
 * Renders from `MODULE_CATALOG` + `useModuleVisibility` — the same single source the /modules
 * launcher uses — so a page is reachable from every surface or none. This component owns no
 * positioning: whoever renders it decides where it sits.
 */
/**
 * The collapsible menu header inside the nav.
 *
 * `minHeight` is 46 only below md: spec 5.1's 46px is a mobile-drawer touch-target rule, and
 * unscoped it added ~2px to every menu header on the desktop sidebar, which must be unchanged.
 */
export const menuHeaderSx = {
    color: 'inherit',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 20px',
    minHeight: { xs: 46, md: 'auto' },
    cursor: 'pointer',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
};

const SidebarNav = ({ collapsed, onNavigate }: SidebarNavProps) => {
    const location = useLocation();
    const { visibility, enabledModules } = useModuleVisibility();
    const { roles } = useAuth();
    const mainModules = (roles ?? []).flatMap(r => r.mainModules ?? []);
    const entries = navEntriesFor(visibility, enabledModules, mainModules);

    const [openMenus, setOpenMenus] = useState<Record<string, boolean>>(() => {
        const seed: Record<string, boolean> = {};
        entries.forEach(({ card, links }) => {
            seed[card.key] = isMenuActive(card, links, location.pathname);
        });
        return seed;
    });

    const toggleMenu = (key: string) =>
        setOpenMenus(prev => ({ ...prev, [key]: !prev[key] }));

    return (
        <nav className="nav">
            {entries.map(({ card, links }) => {
                const label = card.nav?.label ?? card.label;

                // No menu worth opening: link straight to the only page the user can reach.
                if (links.length <= 1) {
                    const path = links.length === 1 ? links[0].path : card.path;
                    return (
                        <Link
                            key={card.key}
                            to={path}
                            onClick={onNavigate}
                            className={`menuItem ${location.pathname === path ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
                            title={collapsed ? label : ''}
                        >
                            <span className="icon">{card.nav!.emoji}</span>
                            {!collapsed && label}
                        </Link>
                    );
                }

                // Collapsed to the icon rail there is nowhere to expand into, so the icon links
                // to the module's launcher page, which lists the same pages as cards.
                if (collapsed) {
                    return (
                        <Link
                            key={card.key}
                            to={`/modules/${card.key}`}
                            onClick={onNavigate}
                            className="menuItem collapsed"
                            title={label}
                        >
                            <span className="icon">{card.nav!.emoji}</span>
                        </Link>
                    );
                }

                const open = openMenus[card.key];
                return (
                    <div key={card.key} className="nav-menu">
                        <List component="nav" sx={{ padding: 0 }}>
                            <ListItem
                                component="div"
                                onClick={() => toggleMenu(card.key)}
                                sx={menuHeaderSx}
                            >
                                <span className="icon">{card.nav!.emoji}</span>
                                <ListItemText primary={label} />
                                {open ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {links.map(link => (
                                        <Link
                                            key={link.path}
                                            to={link.path}
                                            onClick={onNavigate}
                                            className={`menuItem submenu ${location.pathname === link.path ? 'active' : ''}`}
                                        >
                                            <span className="icon">{link.emoji}</span>
                                            {link.label}
                                        </Link>
                                    ))}
                                </List>
                            </Collapse>
                        </List>
                    </div>
                );
            })}
        </nav>
    );
};

export default SidebarNav;
